import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const DEVICE_KEY = 'equiphoto_device_id';

export const getDeviceId = (): string => {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id || !/^[A-Za-z0-9-]{16,64}$/.test(id)) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
};

/** Photo id from a thumbnail URL (last numeric path segment). */
export const photoIdFromUrl = (url: string): number | null => {
  const m = url.match(/\/(\d+)(?:\.[\w-]+)?(?:\?.*)?$/);
  return m ? Number(m[1]) : null;
};

type LikeChange = { eventId: string; photoId: number; delta: number };

/** Fire-and-forget public like counter; optimistic UI via a window event. */
export const sendPhotoLike = (eventId: string, photoId: number, liked: boolean) => {
  window.dispatchEvent(new CustomEvent<LikeChange>('photoLikeChanged', { detail: { eventId, photoId, delta: liked ? 1 : -1 } }));
  (supabase.rpc as any)('like_photo', { _event_id: eventId, _photo_id: photoId, _device_id: getDeviceId(), _liked: liked })
    .then(({ error }: { error: unknown }) => { if (error) console.warn('like_photo failed', error); });
};

type Counts = Record<number, number>;

export const usePhotoLikeCounts = (eventId: string | undefined, photoIds: (number | null | undefined)[]) => {
  const qc = useQueryClient();
  const ids = useMemo(() => Array.from(new Set(photoIds.filter((x): x is number => typeof x === 'number'))).sort((a, b) => a - b), [photoIds]);
  const key = ['photo-like-counts', eventId, ids.join(',')];

  const q = useQuery({
    queryKey: key,
    enabled: !!eventId && ids.length > 0,
    staleTime: 60_000,
    queryFn: async (): Promise<Counts> => {
      const out: Counts = {};
      for (let i = 0; i < ids.length; i += 500) {
        const { data, error } = await (supabase.rpc as any)('get_photo_like_counts', { _event_id: eventId, _photo_ids: ids.slice(i, i + 500) });
        if (error) throw error;
        (data || []).forEach((r: { photo_id: number; likes: number }) => { out[r.photo_id] = Number(r.likes); });
      }
      return out;
    },
  });

  useEffect(() => {
    const on = (e: Event) => {
      const { eventId: ev, photoId, delta } = (e as CustomEvent<LikeChange>).detail;
      if (ev !== eventId) return;
      qc.setQueriesData<Counts>({ queryKey: ['photo-like-counts', eventId] }, old => {
        if (!old) return old;
        return { ...old, [photoId]: Math.max(0, (old[photoId] || 0) + delta) };
      });
    };
    window.addEventListener('photoLikeChanged', on);
    return () => window.removeEventListener('photoLikeChanged', on);
  }, [eventId, qc]);

  return q.data || {};
};
